import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Card, CardContent } from '../../../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { BarChart2, Cable, Package, Layers, FileText, Wrench, Flame, Shield, Ruler } from 'lucide-react';
import { getMaterialTraceabilityAllSummary, getMaterialTraceabilityAllData } from '../../../redux/Project/materialsData/action';
import { PipeDashboardFilters } from './PipeDashboardFilters';
import { PipeDashboardTable, MaterialCategory } from './PipeDashboardTable';

type SummaryData = {
  totalMaterials?: number;
  totalAvailable?: number;
  totalConsumed?: number;
  projectSummary?: {
    totalProjects?: number;
    projects?: Array<{
      projectId: number;
      projectName: string;
      materialCount: number;
      availableCount: number;
      consumedCount: number;
    }>;
  };
  pipeSummary?: {
    totalPipes?: number;
    pipes?: Array<{
      pipeId: number;
      pipeSize: number;
      wallThickness: number;
      materialCount: number;
      availableCount: number;
      consumedCount: number;
    }>;
  };
  categorySummary?: {
    totalCategories?: number;
    [key: string]: unknown;
  };
};

const CATEGORY_KEYS = ['Pipe', 'Forge', 'Gas', 'Wire Lot', 'Coating', 'NDE'];

const CATEGORY_TO_DATA_KEY: Record<MaterialCategory, string> = {
  'Pipe': 'pipes',
  'Forge': 'forge',
  'Gas': 'gas',
  'Wire Lot': 'wireLot',
  'Coating': 'coating',
};

const DASHBOARD_TABS: { key: MaterialCategory; label: string; icon: React.ElementType }[] = [
  { key: 'Pipe', label: 'Pipe', icon: FileText },
  { key: 'Forge', label: 'Forging', icon: Wrench },
  { key: 'Gas', label: 'Gas', icon: Flame },
  { key: 'Wire Lot', label: 'Wire Lot', icon: Cable },
  { key: 'Coating', label: 'Coating', icon: Shield },
];

export const PipeDashboardSummary: React.FC = () => {
  const {
    materialTraceabilitySummary,
    materialTraceabilitySummaryLoading,
    materialTraceabilitySummaryError
  } = useSelector((state: { materialsData?: Record<string, unknown> }) =>
    Object.assign(
      {},
      {
        materialTraceabilitySummary: null,
        materialTraceabilitySummaryLoading: false,
        materialTraceabilitySummaryError: null
      },
      state.materialsData || {}
    )
  ) as {
    materialTraceabilitySummary: SummaryData | null;
    materialTraceabilitySummaryLoading: boolean;
    materialTraceabilitySummaryError: string | null;
  };

  const {
    materialTraceabilityAllData,
    materialTraceabilityAllTotal,
    materialTraceabilityAllDataLoading,
    materialTraceabilityAllDataError
  } = useSelector((state: { materialsData?: Record<string, unknown> }) =>
    state.materialsData || {}
  ) as {
    materialTraceabilityAllData: Record<string, unknown[]>;
    materialTraceabilityAllTotal: number;
    materialTraceabilityAllDataLoading: boolean;
    materialTraceabilityAllDataError: string | null;
  };

  const summary = materialTraceabilitySummary as SummaryData | null;
  const projectSummary = summary?.projectSummary;
  const pipeSummary = summary?.pipeSummary;
  const categorySummary = summary?.categorySummary;

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState('material_id');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  const [selectedProjectNames, setSelectedProjectNames] = useState<string[]>([]);
  const [selectedPipeSizes, setSelectedPipeSizes] = useState<string[]>([]);
  const [selectedWallThickness, setSelectedWallThickness] = useState<string[]>([]);
  const [pipeFilters, setPipeFilters] = useState<Array<{ pipeLength: number; wallThickness: number }>>([]);
  const [pipeFitterOnly, setPipeFitterOnly] = useState(false);
  const [activeCategory, setActiveCategory] = useState<MaterialCategory>('Pipe');

  const projectNames = useMemo(
    () => projectSummary?.projects?.map((p) => p.projectName) ?? [],
    [projectSummary]
  );

  const uniquePipeSizes = useMemo(() => {
    const set = new Set<number>();
    pipeSummary?.pipes?.forEach((p) => set.add(p.pipeSize));
    return Array.from(set).sort((a, b) => a - b);
  }, [pipeSummary]);

  const uniqueWallThickness = useMemo(() => {
    const set = new Set<number>();
    pipeSummary?.pipes?.forEach((p) => set.add(p.wallThickness));
    return Array.from(set).sort((a, b) => a - b);
  }, [pipeSummary]);

  const hasActiveFilters = useMemo(
    () =>
      selectedProjectNames.length > 0 ||
      selectedPipeSizes.length > 0 ||
      selectedWallThickness.length > 0 ||
      pipeFilters.length > 0 ||
      pipeFitterOnly,
    [selectedProjectNames, selectedPipeSizes, selectedWallThickness, pipeFilters, pipeFitterOnly]
  );

  const buildPayload = useCallback(() => {
    const combos: Array<{ pipeLength: number; wallThickness: number }> = [...pipeFilters];
    if (selectedPipeSizes.length > 0 && selectedWallThickness.length > 0) {
      selectedPipeSizes.forEach((s) => {
        selectedWallThickness.forEach((w) => {
          combos.push({ pipeLength: parseFloat(s), wallThickness: parseFloat(w) });
        });
      });
    }
    return {
      projectNames: selectedProjectNames,
      pipeFilters: combos,
      pageSize,
      page: currentPage,
      sortBy,
      sortOrder,
      materialCategory: activeCategory,
      pipeFitterOnly
    };
  }, [
    selectedProjectNames,
    selectedPipeSizes,
    selectedWallThickness,
    pipeFilters,
    pipeFitterOnly,
    activeCategory,
    pageSize,
    currentPage,
    sortBy,
    sortOrder
  ]);

  const dispatch = useDispatch();

  const fetchTableData = useCallback(() => {
    dispatch(getMaterialTraceabilityAllData(buildPayload()));
  }, [dispatch, buildPayload]);

  useEffect(() => {
    dispatch(getMaterialTraceabilityAllSummary({}));
  }, [dispatch]);

  useEffect(() => {
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, activeCategory]);

  const handleApplyFilters = useCallback(() => {
    setCurrentPage(1);
    fetchTableData();
  }, [fetchTableData]);

  const handleResetFilters = useCallback(() => {
    setSelectedProjectNames([]);
    setSelectedPipeSizes([]);
    setSelectedWallThickness([]);
    setPipeFilters([]);
    setPipeFitterOnly(false);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleDocumentClick = useCallback((row: unknown) => {
    console.log('Document click', row);
  }, []);

  const totalMaterials = summary?.totalMaterials ?? 0;
  const totalAvailable = summary?.totalAvailable ?? 0;
  const totalConsumed = summary?.totalConsumed ?? 0;

  const dataKey = CATEGORY_TO_DATA_KEY[activeCategory];
  const dataByCategory = materialTraceabilityAllData as { data?: Record<string, unknown[]> };
  const categoryData = dataByCategory.data?.[dataKey];
  const tableData = (Array.isArray(categoryData) ? categoryData : []) as Array<Record<string, unknown>>;

  if (materialTraceabilitySummaryLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i} className="border border-gray-200 bg-white animate-pulse">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-gray-200" />
                  <div className="flex-1">
                    <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
                    <div className="h-6 w-12 bg-gray-200 rounded" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-8">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e04530]" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (materialTraceabilitySummaryError) {
    return (
      <Card className="border border-red-200 bg-red-50">
        <CardContent className="p-6">
          <p className="text-red-600">{materialTraceabilitySummaryError}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#e04530]/10">
                <Cable className="h-5 w-5 text-[#e04530]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total projects</p>
                <p className="text-xl font-semibold text-gray-900">
                  {projectSummary?.totalProjects ?? '—'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Ruler className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total pipes</p>
                <p className="text-xl font-semibold text-gray-900">{pipeSummary?.totalPipes ?? '—'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total materials</p>
                <p className="text-xl font-semibold text-gray-900">{totalMaterials}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <BarChart2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Available</p>
                <p className="text-xl font-semibold text-gray-900">{totalAvailable}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Layers className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Consumed</p>
                <p className="text-xl font-semibold text-gray-900">{totalConsumed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {materialTraceabilityAllDataError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">
          {materialTraceabilityAllDataError}
        </div>
      )}

      <div className="space-y-4">
        <PipeDashboardFilters
          projectNames={projectNames}
          uniquePipeSizes={uniquePipeSizes}
          uniqueWallThickness={uniqueWallThickness}
          pipeFilters={pipeFilters}
          selectedProjectNames={selectedProjectNames}
          selectedPipeSizes={selectedPipeSizes}
          selectedWallThickness={selectedWallThickness}
          pipeFitterOnly={pipeFitterOnly}
          onProjectNamesChange={setSelectedProjectNames}
          onPipeSizesChange={setSelectedPipeSizes}
          onWallThicknessChange={setSelectedWallThickness}
          onPipeFiltersChange={setPipeFilters}
          onPipeFitterOnlyChange={setPipeFitterOnly}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
          filteredCount={materialTraceabilityAllTotal}
          hasActiveFilters={hasActiveFilters}
        />
        <Tabs value={activeCategory} onValueChange={(v) => { setActiveCategory(v as MaterialCategory); setCurrentPage(1); }}>
          <div className="sticky top-0 bg-white rounded-t-lg z-10 border-b border-gray-200">
            <TabsList className="h-12 w-full justify-start gap-8 border-b border-gray-200 bg-transparent px-0 py-10">
              {DASHBOARD_TABS.map(({ key, label, icon: Icon }) => (
                <TabsTrigger
                  key={key}
                  value={key}
                  className="data-[state=active]:border-[#e04530] data-[state=active]:text-[#e04530] relative px-1 pb-3 pt-2 text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px]"
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <div className="mt-4">
            <PipeDashboardTable
              data={tableData}
              totalItems={materialTraceabilityAllTotal}
              currentPage={currentPage}
              pageSize={pageSize}
              loading={materialTraceabilityAllDataLoading}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onDocumentClick={handleDocumentClick}
              pipeFitterOnly={pipeFitterOnly && activeCategory === 'Pipe'}
              category={activeCategory}
            />
          </div>
        </Tabs>
      </div>

      {/* {categorySummary && (
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Category Summary</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {CATEGORY_KEYS.filter((key) => typeof categorySummary[key] === 'object').map(
                (key) => {
                  const cat = categorySummary[key] as {
                    total?: number;
                    available?: number;
                    consumed?: number;
                  };
                  if (!cat || typeof cat !== 'object') return null;
                  return (
                    <div
                      key={key}
                      className="p-4 rounded-lg border border-gray-200 bg-gray-50/50"
                    >
                      <p className="text-sm font-medium text-gray-700 mb-2">{key}</p>
                      <p className="text-xs text-gray-500">Total: {cat.total ?? 0}</p>
                      <p className="text-xs text-gray-500">Available: {cat.available ?? 0}</p>
                      <p className="text-xs text-gray-500">Consumed: {cat.consumed ?? 0}</p>
                    </div>
                  );
                }
              )}
            </div>
          </CardContent>
        </Card>
      )} */}
    </div>
  );
};
